import { lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { Toaster as RadixToaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/contexts/AuthContext";
import { RigContextProvider } from "@/contexts/RigContext";
import { RigAssistantChat } from "@/components/RigAssistantChat";
import { PageLoader } from "@/components/PageLoader";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Header } from "@/components/Header";
import { ProjectsPageSkeleton } from "@/components/ProjectsPageSkeleton";
import { GlobalAnnouncementBanner } from "@/components/GlobalAnnouncementBanner";
import { MicrosoftClarity } from "@/components/MicrosoftClarity";

// Eager imports - critical paths that need fast initial load
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";

// Lazy loaded pages - User Routes
const AuthConfirm = lazy(() => import("./pages/AuthConfirm"));
const RigView = lazy(() => import("./pages/RigView"));
const RigChecklistsPage = lazy(() => import("./pages/RigChecklistsPage"));
const RigChecklistPage = lazy(() => import("./pages/RigChecklistPage"));
const RigQuotePage = lazy(() => import("./pages/RigQuotePage"));
const RigHistoryPage = lazy(() => import("./pages/RigHistoryPage"));
const ProjectsPage = lazy(() => import("./pages/ProjectsPage"));
const ProjectRigsPage = lazy(() => import("./pages/ProjectRigsPage"));
const CameraSelectPage = lazy(() => import("./pages/CameraSelectPage"));
const RigBuilderPage = lazy(() => import("./pages/RigBuilderPage"));
const GuidedBuilderPage = lazy(() => import("./pages/GuidedBuilderPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const CheckoutCancel = lazy(() => import("./pages/CheckoutCancel"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const GalleryPostPage = lazy(() => import("./pages/GalleryPostPage"));
const MyGearPage = lazy(() => import("./pages/MyGearPage"));
const DocsPage = lazy(() => import("./pages/DocsPage"));
const ChangelogPage = lazy(() => import("./pages/ChangelogPage"));
const TeamPage = lazy(() => import("./pages/TeamPage"));
const RateFinderPage = lazy(() => import("./pages/RateFinderPage"));
const PublicProfilePage = lazy(() => import("./pages/PublicProfilePage"));

// Lazy loaded pages - Admin Routes
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const EquipmentPage = lazy(() => import("./pages/admin/EquipmentPage"));
const CompatibilityPage = lazy(() => import("./pages/admin/CompatibilityPage"));
const UsersPage = lazy(() => import("./pages/admin/UsersPage"));
const PlansPage = lazy(() => import("./pages/admin/PlansPage"));
const GeneralPage = lazy(() => import("./pages/admin/GeneralPage"));
const TeamsPage = lazy(() => import("./pages/admin/TeamsPage"));
const AdminGuidedBuilderPage = lazy(() => import("./pages/admin/GuidedBuilderPage"));
const BuilderSettingsPage = lazy(() => import("./pages/admin/BuilderSettingsPage"));
const ChatbotPage = lazy(() => import("./pages/admin/ChatbotPage"));
const NotificationsPage = lazy(() => import("./pages/admin/NotificationsPage"));
const AdminSupportPage = lazy(() => import("./pages/admin/SupportPage"));
const AdminGalleryPage = lazy(() => import("./pages/admin/GalleryPage"));
const RentalTrackingPage = lazy(() => import("./pages/admin/RentalTrackingPage"));
const UserGearPage = lazy(() => import("./pages/admin/UserGearPage"));
const RecommendationsPage = lazy(() => import("./pages/admin/RecommendationsPage"));
const WebmailPage = lazy(() => import("./pages/admin/WebmailPage"));
const AnnouncementsPage = lazy(() => import("./pages/admin/AnnouncementsPage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 5 minutes stale time reduces unnecessary refetches
      staleTime: 5 * 60 * 1000,
      // 30 minutes garbage collection time
      gcTime: 30 * 60 * 1000,
      // Disable refetch on window focus in production
      refetchOnWindowFocus: import.meta.env.PROD ? false : true,
      // Retry failed requests once
      retry: 1,
    },
  },
});

const App = () => (
  <ErrorBoundary>
    <HelmetProvider>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <RigContextProvider>
              <TooltipProvider>
                <SonnerToaster />
                <RadixToaster />
                <BrowserRouter>
                  <GlobalAnnouncementBanner />
                  <MicrosoftClarity />
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/auth/confirm" element={<Suspense fallback={<PageLoader />}><AuthConfirm /></Suspense>} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/projects" element={
                      <Suspense fallback={
                        <div className="min-h-screen bg-background">
                          <Header onLogoClick={() => {}} />
                          <main className="container mx-auto px-4 pb-12 max-w-4xl">
                            <ProjectsPageSkeleton />
                          </main>
                        </div>
                      }>
                        <ProjectsPage />
                      </Suspense>
                    } />
                    
                    {/* Project Routes */}
                    <Route path="/project/:projectId" element={<Suspense fallback={<PageLoader />}><ProjectRigsPage /></Suspense>} />
                    <Route path="/project/:projectId/new" element={<Suspense fallback={<PageLoader />}><CameraSelectPage /></Suspense>} />
                    <Route path="/project/:projectId/build" element={<Suspense fallback={<PageLoader />}><RigBuilderPage /></Suspense>} />
                    <Route path="/project/:projectId/edit/:rigId" element={<Suspense fallback={<PageLoader />}><RigBuilderPage /></Suspense>} />
                    
                    {/* Guided Builder */}
                    <Route path="/guided" element={<Suspense fallback={<PageLoader />}><GuidedBuilderPage /></Suspense>} />
                    <Route path="/guided/:projectId" element={<Suspense fallback={<PageLoader />}><GuidedBuilderPage /></Suspense>} />
                    
                    {/* Rig View */}
                    <Route path="/rig/:projectId/:rigId" element={<Suspense fallback={<PageLoader />}><RigView /></Suspense>} />
                    <Route path="/rig/:projectId/:rigId/checklists" element={<Suspense fallback={<PageLoader />}><RigChecklistsPage /></Suspense>} />
                    <Route path="/rig/:projectId/:rigId/checklists/:checklistId" element={<Suspense fallback={<PageLoader />}><RigChecklistPage /></Suspense>} />
                    <Route path="/rig/:projectId/:rigId/checklists/:checklistId/quote/:quoteId" element={<Suspense fallback={<PageLoader />}><RigQuotePage /></Suspense>} />
                    <Route path="/rig/:projectId/:rigId/history" element={<Suspense fallback={<PageLoader />}><RigHistoryPage /></Suspense>} />
                    
                    {/* Pricing, Profile & Support */}
                    <Route path="/pricing" element={<Suspense fallback={<PageLoader />}><PricingPage /></Suspense>} />
                    <Route path="/settings" element={<Suspense fallback={<PageLoader />}><SettingsPage /></Suspense>} />
                    <Route path="/support" element={<Suspense fallback={<PageLoader />}><SupportPage /></Suspense>} />
                    <Route path="/gallery" element={<Suspense fallback={<PageLoader />}><GalleryPage /></Suspense>} />
                    <Route path="/gallery/:postId" element={<Suspense fallback={<PageLoader />}><GalleryPostPage /></Suspense>} />
                    <Route path="/my-gear" element={
                      <Suspense fallback={<PageLoader />}>
                        <MyGearPage />
                      </Suspense>
                    } />
                    <Route path="/rate-finder" element={
                      <Suspense fallback={<PageLoader />}>
                        <RateFinderPage />
                      </Suspense>
                    } />
                    <Route path="/docs" element={<Suspense fallback={<PageLoader />}><DocsPage /></Suspense>} />
                    <Route path="/docs/:sectionId" element={<Suspense fallback={<PageLoader />}><DocsPage /></Suspense>} />
                    <Route path="/changelog" element={<Suspense fallback={<PageLoader />}><ChangelogPage /></Suspense>} />
                    <Route path="/team" element={<Suspense fallback={<PageLoader />}><TeamPage /></Suspense>} />
                    <Route path="/u/:identifier" element={<Suspense fallback={<PageLoader />}><PublicProfilePage /></Suspense>} />
                    <Route path="/checkout-success" element={<Suspense fallback={<PageLoader />}><CheckoutSuccess /></Suspense>} />
                    <Route path="/checkout-cancel" element={<Suspense fallback={<PageLoader />}><CheckoutCancel /></Suspense>} />
                    
                    {/* Admin Routes */}
                    <Route path="/admin" element={<Suspense fallback={<PageLoader />}><AdminLayout /></Suspense>}>
                      <Route index element={<Suspense fallback={<PageLoader />}><GeneralPage /></Suspense>} />
                      <Route path="equipment" element={<Suspense fallback={<PageLoader />}><EquipmentPage /></Suspense>} />
                      <Route path="compatibility" element={<Suspense fallback={<PageLoader />}><CompatibilityPage /></Suspense>} />
                      <Route path="builder-settings" element={<Suspense fallback={<PageLoader />}><BuilderSettingsPage /></Suspense>} />
                      <Route path="guided-builder" element={<Suspense fallback={<PageLoader />}><AdminGuidedBuilderPage /></Suspense>} />
                      
                      <Route path="chatbot" element={<Suspense fallback={<PageLoader />}><ChatbotPage /></Suspense>} />
                      <Route path="gallery" element={<Suspense fallback={<PageLoader />}><AdminGalleryPage /></Suspense>} />
                      <Route path="rental-tracking" element={<Suspense fallback={<PageLoader />}><RentalTrackingPage /></Suspense>} />
                      <Route path="notifications" element={<Suspense fallback={<PageLoader />}><NotificationsPage /></Suspense>} />
                      <Route path="support" element={<Suspense fallback={<PageLoader />}><AdminSupportPage /></Suspense>} />
                      <Route path="users" element={<Suspense fallback={<PageLoader />}><UsersPage /></Suspense>} />
                      <Route path="teams" element={<Suspense fallback={<PageLoader />}><TeamsPage /></Suspense>} />
                      <Route path="plans" element={<Suspense fallback={<PageLoader />}><PlansPage /></Suspense>} />
                      <Route path="user-gear" element={<Suspense fallback={<PageLoader />}><UserGearPage /></Suspense>} />
                      <Route path="recommendations" element={<Suspense fallback={<PageLoader />}><RecommendationsPage /></Suspense>} />
                      <Route path="webmail" element={<Suspense fallback={<PageLoader />}><WebmailPage /></Suspense>} />
                      <Route path="announcements" element={<Suspense fallback={<PageLoader />}><AnnouncementsPage /></Suspense>} />
                    </Route>
                    
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  <RigAssistantChat />
                </BrowserRouter>
              </TooltipProvider>
            </RigContextProvider>
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </HelmetProvider>
  </ErrorBoundary>
);

export default App;
